import { SpeechSegmenter, speakerChannelProfile } from './speechSegmenter'
import { UtteranceFeed } from './utteranceFeed'
import { SpeakerChannelDetector } from './voiceDetectors'

/** Sample rate of the capture app's audio */
export const CAPTURE_SAMPLE_RATE = 48000
/** Block size of the speech detection for the speaker channels: 10 ms at 48 kHz */
const ANALYSIS_BLOCK_FRAMES = 480
/** Size of one audio callback of the capture processor, in frames (about 85 ms at 48 kHz) */
const OUTPUT_BUFFER_FRAMES = 4096

/**
 * One speaker channel: the separate microphone (`me`) or system audio (`remote`), for its own OpenAI session.
 * `stream` is the audio to send to OpenAI. `utterances` reports when to commit each utterance of this audio,
 * and `utterances.speaker` labels the channel.
 */
export interface SpeakerChannel {
  stream: MediaStream
  utterances: UtteranceFeed
}

/**
 * Plays the capture app's audio into media tracks, so that it can be sent to OpenAI over WebRTC.
 * The app sends audio samples over a WebSocket. This class queues them,
 * and a Web Audio callback copies them into the tracks.
 *
 * Formats:
 * - Mixed (the default): stereo frames with the same mix in both channels. The result is one track with the mix.
 * - Speaker channels (after {@link useSpeakerChannels}): frames with three channels: the mix, the microphone,
 *   and the system audio. The result is the mix track and one track for each speaker channel.
 *   The same callback that writes the samples also detects speech in the speaker channels.
 */
export class CaptureAppPlayback {
  private readonly audioContext = new AudioContext({ sampleRate: CAPTURE_SAMPLE_RATE })
  private readonly processor: ScriptProcessorNode
  /** Audio chunks received from the capture app, waiting to be played into the media tracks. */
  private readonly queue: { chunks: Float32Array[]; readPos: number } = { chunks: [], readPos: 0 }
  /** Number of frames received from the app, also frames that still wait in the queue. */
  private receivedFrames = 0
  /** Number of samples in one frame: 2 for the mixed format, 3 for speaker channels. */
  private frameChannels = 2
  /** Speech detection of the speaker channels. Null in the mixed format. */
  private segmenter: SpeechSegmenter | null = null
  /** The three output streams: mix, microphone, and system audio. In the mixed format, the last two are silent. */
  private readonly outputStreams: MediaStream[]
  /** The mix of all audio: for ordinary transcription and for filling the playbook. */
  readonly stream: MediaStream
  /** The microphone and system-audio channels. Empty in the mixed format. */
  speakerChannels: SpeakerChannel[] = []

  /**
   * Build the audio graph for both formats, before the app sends audio.
   * Audio that arrives while the graph starts would wait in the queue, and delay all later audio.
   */
  constructor() {
    const { audioContext } = this
    const captured = [new Float32Array(OUTPUT_BUFFER_FRAMES), new Float32Array(OUTPUT_BUFFER_FRAMES)]

    this.processor = audioContext.createScriptProcessor(OUTPUT_BUFFER_FRAMES, 1, 3)
    this.processor.onaudioprocess = (e: AudioProcessingEvent) => {
      const output = [0, 1, 2].map(channel => e.outputBuffer.getChannelData(channel))
      const feeds = this.speakerChannels.map(channel => channel.utterances)
      // The browser has now sent the samples of the previous callback. Report the boundaries found in them.
      feeds.forEach(feed => feed.flush())
      const frames = this.drainQueue(output, captured)
      // Analyze only real capture audio, not the silence output for an empty queue.
      // That silence is not part of the captured audio, so it would shift the positions and spoil the noise level.
      if (this.segmenter) {
        const result = this.segmenter.process(captured.map(samples => samples.subarray(0, frames)))
        feeds.forEach(feed => feed.accept(result))
      }
    }

    // Split the three output channels into three mono media tracks: mix, microphone, and system audio.
    const destinations = [0, 1, 2].map(() => {
      const destination = audioContext.createMediaStreamDestination()
      destination.channelCount = 1
      return destination
    })
    const splitter = audioContext.createChannelSplitter(3)
    this.processor.connect(splitter)
    destinations.forEach((destination, index) => splitter.connect(destination, index))
    this.outputStreams = destinations.map(destination => destination.stream)
    this.stream = this.outputStreams[0]
  }

  /**
   * Switch to the format with speaker channels, and start the speech detection for them.
   * Call this before the app sends audio in this format: when its format message arrives.
   */
  useSpeakerChannels() {
    this.frameChannels = 3
    // Detect speech in the speaker channels, and report where utterances end to their OpenAI sessions.
    const segmenter = new SpeechSegmenter(
      [new SpeakerChannelDetector(true), new SpeakerChannelDetector(false)],
      speakerChannelProfile,
      CAPTURE_SAMPLE_RATE,
      ANALYSIS_BLOCK_FRAMES,
    )
    this.segmenter = segmenter
    this.speakerChannels = (['me', 'remote'] as const).map((speaker, input) => ({
      stream: this.outputStreams[input + 1],
      utterances: new UtteranceFeed(segmenter, input, () => this.receivedFrames, speaker),
    }))
  }

  /** Queue a binary audio message of the capture app: 32-bit float samples, interleaved by frame. */
  push(data: ArrayBuffer) {
    this.queue.chunks.push(new Float32Array(data))
    this.receivedFrames += data.byteLength / 4 / this.frameChannels
  }

  /** Start the audio callbacks. */
  async start() {
    await this.audioContext.resume()
  }

  /** Stop the audio callbacks and end all tracks. */
  stop() {
    this.processor.disconnect()
    void this.audioContext.close()
    this.outputStreams.forEach(stream => stream.getTracks().forEach(track => track.stop()))
  }

  /**
   * Copy queued capture audio into the three output channels: mix, microphone, and system audio.
   * Also copy the microphone and system samples into `captured`, for speech detection.
   * When the queue is empty, output silence.
   * Return the number of captured frames.
   */
  private drainQueue([mix, mic, system]: Float32Array[], captured: Float32Array[]) {
    const { queue, frameChannels } = this
    let outIdx = 0
    let captureFrames = 0
    while (outIdx < mix.length) {
      if (queue.chunks.length === 0) {
        mix[outIdx] = mic[outIdx] = system[outIdx] = 0
        outIdx++
        continue
      }

      const chunk = queue.chunks[0]
      const chunkFrames = chunk.length / frameChannels

      if (queue.readPos >= chunkFrames) {
        queue.chunks.shift()
        queue.readPos = 0
        continue
      }

      // Three channels: mix, microphone, system audio. Mixed format: the same mix in both stereo channels.
      const i = queue.readPos * frameChannels
      mix[outIdx] = frameChannels === 3 ? chunk[i] : (chunk[i] + chunk[i + 1]) * 0.5
      mic[outIdx] = captured[0][captureFrames] = frameChannels === 3 ? chunk[i + 1] : 0
      system[outIdx] = captured[1][captureFrames] = frameChannels === 3 ? chunk[i + 2] : 0
      outIdx++
      queue.readPos++
      captureFrames++
    }
    return captureFrames
  }
}
